import { html, nothing, svg, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { UiElement, createEvent, type ZbiChangeEvent, type ZbiClickEvent } from "@zebrabi/ui-components";
import type { FieldKind } from "../definitions";
import styles from "./field.styles";

/** Three columns side by side: the Values entry stands for one column per group */
const COLUMNS_ICON = svg`<svg viewBox="0 0 12 8" aria-hidden="true"><rect x="0.5" y="0.5" width="2.5" height="7" /><rect x="4.75" y="0.5" width="2.5" height="7" /><rect x="9" y="0.5" width="2.5" height="7" /></svg>`;

/** The marker in front of the label, by kind: text columns, measure columns and the synthetic Values entry */
const KIND_MARKERS: Record<FieldKind, string | TemplateResult> = { text: "ABC", measure: "123", values: COLUMNS_ICON };

/**
 * One field: a label with an optional kind marker, a pointer cursor when the field can be dragged, and, in the Fields
 * list, a checkbox that tells whether the field sits in a bucket. A bucket field may show × to leave its bucket.
 * The checkbox emits `zbi-change` with the new tick, the × emits `zbi-click`; neither starts a drag.
 */
export class FieldsPaneField extends UiElement {
    static styles = styles;

    @property({ type: String })
    public label = "";

    @property({ type: Boolean, reflect: true })
    public movable = false;

    /** The kind the marker shows; no marker when unset. Reflected, so the Values entry can be styled as a heading */
    @property({ type: String, reflect: true })
    public kind?: FieldKind;

    /** Indents a Fields list entry under the Values entry it belongs to */
    @property({ type: Boolean, reflect: true })
    public nested = false;

    /** Shows the checkbox of a Fields list entry; it is disabled for a fixed field */
    @property({ type: Boolean })
    public checkbox = false;

    @property({ type: Boolean })
    public checked = false;

    /** Shows the × that takes the field out of its bucket */
    @property({ type: Boolean })
    public removable = false;

    /** Draws the insertion line of a reorder drag above or below this field */
    @property({ type: String, reflect: true })
    public insertion?: "before" | "after";

    protected render(): TemplateResult {
        return html`
            ${this.renderCheckbox()} ${this.kind ? html`<span class="kind">${KIND_MARKERS[this.kind]}</span>` : nothing}
            <span class="label">${this.label}</span>
            ${this.renderRemove()}
        `;
    }

    private renderCheckbox(): TemplateResult | typeof nothing {
        if (!this.checkbox) {
            return nothing;
        }
        return html`
            <input
                type="checkbox"
                aria-label=${`Use ${this.label}`}
                .checked=${this.checked}
                ?disabled=${!this.movable}
                @pointerdown=${stopPropagation}
                @change=${this.handleToggle}
            />
        `;
    }

    private renderRemove(): TemplateResult | typeof nothing {
        if (!this.removable) {
            return nothing;
        }
        return html`
            <button class="remove" aria-label=${`Remove ${this.label}`} @pointerdown=${stopPropagation} @click=${this.handleRemove}>
                ×
            </button>
        `;
    }

    /** The checkbox is controlled: it shows `checked` until the host passes a new value, so the tick is put back before the event goes out */
    private handleToggle(event: Event): void {
        const input = event.currentTarget as HTMLInputElement;
        const value = input.checked;
        input.checked = this.checked;
        this.dispatchEvent(createEvent<ZbiChangeEvent<boolean>>("zbi-change", { name: "checked", value }));
    }

    private handleRemove(): void {
        this.dispatchEvent(createEvent<ZbiClickEvent>("zbi-click", { name: "remove", value: "" }));
    }
}

/** The checkbox and the × are clicked, not dragged: the press never reaches the pane's drag handler */
function stopPropagation(event: Event): void {
    event.stopPropagation();
}
